// src/components/UserServicesDashboard.js
import React, {
  useContext, useEffect, useState, useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { AuthContext } from '../context/AuthContext';
import { fetchUserDetails, fetchUserCategory } from '../services/apiService';
import menuItemsByService from '../categories/serviceMenus';
import { categoryKeysForProgressTotals } from '../categories/categoryMappings';
import categoryIdMapping from '../categories/categoryIdMapping';
import { useService } from '../context/ServiceContext';

const UserServicesDashboard = () => {
  const { authTokens, impersonatedUserId } = useContext(AuthContext);
  const [services, setServices] = useState([]);
  const [categoryIndicators, setCategoryIndicators] = useState({});
  const [totalIndicators, setTotalIndicators] = useState({});
  const navigate = useNavigate();
  const { selectService } = useService();

  const fetchUserCategoryData = useCallback(async (userIdToFetch, category) => {
    if (!categoryIdMapping[category]) return undefined;

    const categoryId = categoryIdMapping[category];
    try {
      const userCategory = await fetchUserCategory(authTokens, userIdToFetch, categoryId);
      return userCategory;
    } catch (error) {
      console.error(`Error fetching category data for ${category}:`, error);
      return undefined;
    }
  }, [authTokens]);

  useEffect(() => {
    const serviceOrder = {
      'Anexo 30 y 31 2022': 1,
      'Anexo 30 y 31 2023': 2,
      'Anexo 30 y 31 2024': 3,
      'Anexo 30 y 31 2025': 4,
      'Anexo 21 y 22 rmf 2026': 5,
      'Transporte Anexo 21 y 22 rmf 2026': 6,
      'Distribución Anexo 21 y 22 rmf 2026': 7,
      'Comercializadora Anexo 21 y 22 rmf 2026': 8,
      'Dictamen de Diseño': 9,
      'NOM-016-CRE-2016': 10,
      'Anexo 30 y 31 para transporte o distribución': 11,
    };

    const loadUserServices = async () => {
      if (authTokens && (impersonatedUserId || authTokens.userRole !== 'admin')) {
        try {
          const userIdToFetch = impersonatedUserId || authTokens.userId;
          const userDetails = await fetchUserDetails(userIdToFetch, authTokens);
          const sortedServices = userDetails.services.sort(
            (a, b) => (serviceOrder[a.name] || 0) - (serviceOrder[b.name] || 0),
          );
          setServices(sortedServices);

          const indicatorsPromises = sortedServices.flatMap((service) => {
            const categories = categoryKeysForProgressTotals(service.name);
            return categories.map(async (category) => {
              const userCategory = await fetchUserCategoryData(userIdToFetch, category);
              return { [category]: userCategory || {} };
            });
          });

          const indicatorsArray = await Promise.all(indicatorsPromises);
          const indicators = indicatorsArray.reduce((acc, curr) => ({ ...acc, ...curr }), {});
          setCategoryIndicators(indicators);

          const totals = {};
          sortedServices.forEach((service) => {
            const categories = categoryKeysForProgressTotals(service.name);
            totals[service.name] = categories.reduce((acc, category) => {
              const indicator = indicators[category] || {};
              acc.documents_uploaded += indicator.documents_uploaded || 0;
              acc.documents_completed += indicator.documents_completed || 0;
              acc.documents_total += indicator.documents_total || 0;
              return acc;
            }, { documents_uploaded: 0, documents_completed: 0, documents_total: 0 });
          });
          setTotalIndicators(totals);
        } catch (error) {
          console.error('Error al cargar los servicios del usuario:', error);
        }
      }
    };

    loadUserServices();
  }, [authTokens, impersonatedUserId, fetchUserCategoryData]);

  const handleServiceClick = (serviceName) => {
    selectService(serviceName);
    const firstMenuItem = menuItemsByService[serviceName]?.[0];
    if (firstMenuItem) {
      navigate(firstMenuItem.path);
    }
  };

  const getProgressColor = (uploaded, total) => {
    if (total === 0) return 'gray'; // Gris para casos de 0 de 0
    const percentage = (uploaded / total) * 100;
    if (percentage < 25) return 'red';
    if (percentage < 50) return 'orange';
    if (percentage < 99) return 'yellow';
    return 'green';
  };

  const calculatePercentage = (uploaded, total) => (total === 0 ? 0 : (uploaded / total) * 100);

  let content;
  if (authTokens?.userRole === 'admin' && !impersonatedUserId) {
    content = (
      <div className="text-center text-lg font-semibold">
        Por favor, seleccione un usuario para acceder a la información de los servicios.
      </div>
    );
  } else if (services.length === 0) {
    content = <div className="text-center text-lg font-semibold">Actualmente no se tienen servicios asignados.</div>;
  } else {
    content = (
      <>
        <h3 className="text-xl font-semibold mb-6">Tus Servicios</h3>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          breakpoints={{
            900: {
              slidesPerView: 2,
            },
            1400: {
              slidesPerView: 3,
            },
          }}
          className="service-slider"
        >
          {services.map((service) => (
            <SwiperSlide key={service.id}>
              <div
                className={`
                  w-full bg-white rounded-lg shadow-md hover:shadow-xl
                  transition-all duration-300 transform hover:-translate-y-1
                  border border-gray-100 h-full
                `}
              >
                <div className="p-6">
                  <button
                    type="button"
                    onClick={() => handleServiceClick(service.name)}
                    className="w-full text-left text-xl font-medium text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    {service.name}
                  </button>
                  <div className="mt-6">
                    {categoryKeysForProgressTotals(service.name).map((category) => (
                      <div key={category} className="bg-gray-100 text-black p-2 mt-2 rounded" style={{ display: 'none' }}>
                        <div>
                          Documentos subidos:
                          {categoryIndicators[category]?.documents_uploaded || 0}
                          {' '}
                          de
                          {categoryIndicators[category]?.documents_total || 0}
                        </div>
                        <div>
                          Documentos completados:
                          {categoryIndicators[category]?.documents_completed || 0}
                          {' '}
                          de
                          {categoryIndicators[category]?.documents_total || 0}
                        </div>
                      </div>
                    ))}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium text-gray-500">Documentos subidos</h3>
                          <span className="text-sm font-medium text-gray-900">
                            {totalIndicators[service.name]?.documents_uploaded || 0}
                            {' '}
                            /
                            {' '}
                            {totalIndicators[service.name]?.documents_total || 0}
                          </span>
                        </div>
                        <div
                          className="flex items-center justify-center"
                          style={{
                            width: '120px',
                            height: '120px',
                            margin: '0 auto',
                          }}
                        >
                          <CircularProgressbar
                            value={calculatePercentage(
                              totalIndicators[service.name]?.documents_uploaded || 0,
                              totalIndicators[service.name]?.documents_total || 1,
                            )}
                            styles={buildStyles({
                              pathColor: getProgressColor(
                                totalIndicators[service.name]?.documents_uploaded,
                                totalIndicators[service.name]?.documents_total,
                              ),
                              trailColor: '#f3f4f6',
                              strokeLinecap: 'round',
                            })}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-medium text-gray-500">Documentos completados</h3>
                          <span className="text-sm font-medium text-gray-900">
                            {totalIndicators[service.name]?.documents_completed || 0}
                            {' '}
                            /
                            {' '}
                            {totalIndicators[service.name]?.documents_total || 0}
                          </span>
                        </div>
                        <div
                          className="flex items-center justify-center"
                          style={{
                            width: '120px',
                            height: '120px',
                            margin: '0 auto',
                          }}
                        >
                          <CircularProgressbar
                            value={calculatePercentage(
                              totalIndicators[service.name]?.documents_completed || 0,
                              totalIndicators[service.name]?.documents_total || 1,
                            )}
                            styles={buildStyles({
                              pathColor: getProgressColor(
                                totalIndicators[service.name]?.documents_completed,
                                totalIndicators[service.name]?.documents_total,
                              ),
                              trailColor: '#f3f4f6',
                              strokeLinecap: 'round',
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </>
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-lg p-8 my-4">
      <style>
        {`
          .service-slider {
            padding: 20px 40px 50px !important;
            position: relative;
          }
          .swiper-button-next,
          .swiper-button-prev {
            color: #3B82F6 !important;
            width: 40px !important;
            height: 40px !important;
            background-color: white;
            border-radius: 50%;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .swiper-button-next {
            right: 0 !important;
          }
          .swiper-button-prev {
            left: 0 !important;
          }
          .swiper-button-next::after,
          .swiper-button-prev::after {
            font-size: 20px !important;
            font-weight: bold;
          }
          .swiper-pagination-bullet-active {
            background: #3B82F6 !important;
          }
          .swiper-wrapper {
            padding: 10px 0;
          }
        `}
      </style>
      {content}
    </div>
  );
};

export default UserServicesDashboard;
